import { ProjectDocumentsCardComponent } from '../../../components/project-documents-card/project-documents-card.component';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterEveryRender,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  skip,
  startWith,
  take,
} from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { TestCaseService } from '../../../services/test-case.service';
import { TestCaseLinksService } from '../../../services/test-case-links.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CreateTestCaseLinkRequest,
  FailedLinkDraft,
  LinkSaveFailure,
  SavedLinkDraft,
} from '../../../models/test-case-link.model';
import { ProjectMemberService } from '../../../services/project-member.service';
import { TestStepService } from '../../../services/test-step.service';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { AccountService } from '../../../account/account.service';
import {
  TestCaseAutomationFramework,
  TestCaseAutomationStatusKey,
  SaveFullTestCaseResponse,
  TestCase,
  TestCasePriority,
  TestStep,
} from '../../../models/test-case.model';
import { Requirement } from '../../../models/requirement.model';
import {
  TestCaseFormLoadedFreetext,
  TestCaseFormRaw,
  loadedFreetextOf,
  toSaveFullRequest,
} from './test-case-form.mappers';
import { TestCaseDetailsHistorieComponent } from '../test-case-details-historie/test-case-details-historie.component';
import { RequirementLinkBlockComponent } from '../requirement-link-block/requirement-link-block.component';
import { TestStepTableComponent } from '../test-step-table/test-step-table.component';
import { ModuleEditDrawerComponent } from '../../module/module-edit-drawer/module-edit-drawer.component';
import { TestCaseAutomationCardComponent } from '../test-case-automation-card/test-case-automation-card.component';
import { TestCaseExecutionStatsComponent } from '../test-case-execution-stats/test-case-execution-stats.component';
import { TestCaseUsageComponent } from '../test-case-usage/test-case-usage.component';
import { TestCaseLinksComponent } from '../test-case-links/test-case-links.component';
import { AddLinkModalComponent } from '../add-link-modal/add-link-modal.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { PersonOption } from '../../../components/person-select/person-select.component';
import { ReviewerService } from '../../../services/reviewer.service';
import { ReviewerDTO } from '../../../models/reviewer.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { savedConditionsNotChangedSince, withSavedStepConditions } from '../../../shared/tdb-override-delta';
import { createFormDirtyTracker } from '../../../shared/forms/dirty-tracker';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { requiredNotBlank } from '../../../shared/forms/required-not-blank.validator';
import { isSessionEnded, isStaleVersion, secretColumnError, serverMessage } from '../../../shared/http/server-message';
import {
  NEW_REQUIREMENT_DRAFT_ID,
  RequirementFormDraftStore,
} from '../../requirements/requirement-form/requirement-form-draft.store';
import { KiQualitaetsCheckComponent } from '../../../components/ki/ki-qualitaets-check/ki-qualitaets-check.component';
import { KiQualitaetAenderung } from '../../../models/ki.model';
import { KiMarkerComponent } from '../../../components/ki/ki-marker/ki-marker.component';
import { findRouteParam } from '../../../shared/route-params';
import { ProjektArchiviertHinweisComponent } from '../../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { TestCaseSelectionList } from '../../../services/test-case-selection-list.service';
import { AnforderungAuswahlDialogComponent } from '../../../components/anforderung-auswahl-dialog/anforderung-auswahl-dialog.component';
import {
  AUSWAHLLISTE_ZU_CONTROL,
  TestCaseEigenschaftenComponent,
} from '../test-case-eigenschaften/test-case-eigenschaften.component';

const PFLICHTFELDER = ['title', 'type', 'testType', 'status', 'assigneeUserId'] as const;
const FOKUSSIERBAR = 'input, textarea, select, button, [tabindex]';
const SERVER_FELDER = ['type', 'testType', 'status', 'automationStatus', 'assigneeUserId'] as const;
const SERVER_FEHLER_TEXT: Record<string, string> = {
  SELECTION_VALUE_UNKNOWN: 'testCases.form.properties.valueUnknown',
  SELECTION_VALUE_INACTIVE: 'testCases.form.properties.valueInactive',
  ASSIGNEE_REQUIRED: 'testCases.form.properties.assigneeRequired',
};

function serverFeldFehler(err: unknown): { feld: (typeof SERVER_FELDER)[number]; code: string; text: string } | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 400) return null;
  const feld = err.error?.field as string | undefined;
  const passend = SERVER_FELDER.find((f) => f === feld);
  return passend ? { feld: passend, code: err.error?.code ?? '', text: err.error?.error ?? '' } : null;
}

// Nur beim Link-Typ REQUIREMENT bedeutet 404 eine unbekannte Anforderung (NEX-1493); sonst etwa einen fehlenden Testfall.
function missingRequirementKey(request: CreateTestCaseLinkRequest, err: unknown): string | null {
  return request.type === 'REQUIREMENT' && err instanceof HttpErrorResponse && err.status === 404
    ? request.reference
    : null;
}

function linkSaveFailure(result: { missingKey: string | null; duplicate: boolean }): LinkSaveFailure {
  if (result.missingKey) return 'notFound';
  return result.duplicate ? 'duplicate' : 'other';
}

function isDuplicateRequirementLink(request: CreateTestCaseLinkRequest, err: unknown): boolean {
  return (
    request.type === 'REQUIREMENT' &&
    err instanceof HttpErrorResponse &&
    err.status === 409 &&
    err.error?.code === 'TEST_CASE_LINK_DUPLICATE'
  );
}

function withServerIds(steps: TestStep[], mapping: Record<string, string>): TestStep[] {
  return steps.map((s) => (mapping[s.id] ? { ...s, id: mapping[s.id] } : s));
}

@Component({
  selector: 'app-test-case-form',
  imports: [
    ProjektArchiviertHinweisComponent,
    ProjectDocumentsCardComponent,
    ReactiveFormsModule,
    TranslatePipe,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatSuffix,
    MatInput,
    MatProgressSpinner,
    MatTooltip,
    TestCaseDetailsHistorieComponent,
    RequirementLinkBlockComponent,
    TestStepTableComponent,
    ModuleEditDrawerComponent,
    TestCaseAutomationCardComponent,
    TestCaseExecutionStatsComponent,
    TestCaseUsageComponent,
    TestCaseLinksComponent,
    AddLinkModalComponent,
    SectionTitleComponent,
    TestCaseEigenschaftenComponent,
    Modal,
    ModalEscapeGuardDirective,
    KiQualitaetsCheckComponent,
    KiMarkerComponent,
    AnforderungAuswahlDialogComponent,
  ],
  templateUrl: './test-case-form.component.html',
  styleUrl: './test-case-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);
  private readonly linksService = inject(TestCaseLinksService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly testStepService = inject(TestStepService);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly translateService = inject(TranslateService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly reviewerService = inject(ReviewerService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly accountService = inject(AccountService);
  private readonly draftStore = inject(RequirementFormDraftStore);
  private readonly saveToast = inject(SaveToastService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  private get returnToRequirementId(): string | null {
    const qp = this.route.snapshot.queryParamMap;
    return qp.get('from') === 'anforderung' ? qp.get('anfId') : null;
  }

  readonly isReadOnly = this.projectRoleService.isReadOnly;
  readonly isProjectReader = this.projectRoleService.isProjectReader;
  readonly isArchived = this.projectRoleService.isArchived;
  readonly canEditModule = computed(
    () =>
      !this.isReadOnly() &&
      (this.projectRoleService.isPlatformAdmin() ||
        this.projectRoleService.isTenantAdmin() ||
        this.projectRoleService.role() === 'PROJECT_WRITER' ||
        this.projectRoleService.role() === 'PROJECT_ADMIN'),
  );

  readonly projectId: string;
  /** Der Dialog legt den manuellen Probelauf selbst an; das Formular reicht nur, was er wissen muss. */
  openRunDialog(): void {
    this.automationCard()?.openRunDialog(this.isDirtyView(), this.assignableCurrentUserId());
  }

  private assignableCurrentUserId(): string | null {
    const userId = this.currentUserId();
    return userId && this.personOptions().some((o) => o.id === userId) ? userId : null;
  }

  get testCaseId(): string | null {
    return this.findParam('id');
  }
  get isEdit(): boolean {
    return this.testCaseId !== null;
  }
  private savedTestCaseId: string | null;
  private loadedFreetext: TestCaseFormLoadedFreetext | null = null;

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorText = signal<string | null>(null);
  readonly saveStale = signal(false);
  private loadedVersion: number | null = null;
  readonly missingRequirementKey = signal<string | null>(null);
  readonly duplicateRequirementLink = signal(false);
  readonly testCase = signal<TestCase | null>(null);
  /** Lokaler Draft-Zustand aller Testschritte (NEX-599 Buffered-Save). */
  readonly draftSteps = signal<TestStep[]>([]);
  /** Erst rendern, wenn die Ausgangs-Schritte im draft-State stehen — sonst hydriert der Table mit []. */
  readonly stepsReady = signal(false);

  /** NEX-686: aktuell im Right-Drawer bearbeitetes eingebundenes Modul; null = Drawer zu. */
  readonly editingModuleId = signal<string | null>(null);
  readonly linkedRequirementId = signal<string | null>(null);
  readonly linkedStoryKey = signal<string | null>(null);
  readonly pendingLinkDrafts = signal<CreateTestCaseLinkRequest[]>([]);
  readonly pendingLinkDeletions = signal<string[]>([]);
  /** Child-Referenz fuer Reconciliation nach Partial-Save (NEX-714 Review Runde 2). */
  private readonly linksBlock = viewChild<TestCaseLinksComponent>('linksBlock');
  /** Der Auftrag lebt in der Karte, damit sein Status dort erscheint; die Leiste stößt ihn nur an. */
  protected readonly automationCard = viewChild<TestCaseAutomationCardComponent>('automationCard');
  private readonly requirementBlock = viewChild(RequirementLinkBlockComponent);
  private readonly newRequirementJiraKey = this.jiraKeyOfNewRequirement();
  protected readonly sourceIsNewRequirement = !this.isEdit && this.returnToRequirementId === NEW_REQUIREMENT_DRAFT_ID;
  /** Eine nicht ladbare (etwa gelöschte) primäre Anforderung zeigt das Feld leer; entfernen lässt sie sich per Kreuz (NEX-2610). */
  private readonly primaereAnforderungFehlt = computed(
    () =>
      !!this.linkedRequirementId() && (!!this.requirementBlock()?.loadError() || !!this.requirementBlock()?.deleted()),
  );
  private readonly gewaehlteAnforderung = signal<Requirement | null>(null);
  readonly anforderungAuswahlOffen = signal(false);
  readonly anforderungSuchtext = signal('');
  private readonly anforderungFeld = viewChild<ElementRef<HTMLInputElement>>('anforderungFeld');
  private readonly titelFeld = viewChild<ElementRef<HTMLInputElement>>('titelFeld');
  protected readonly anforderungAnzeige = computed(() => {
    const id = this.linkedRequirementId();
    if (!id || this.primaereAnforderungFehlt()) return null;
    const gewaehlt = this.gewaehlteAnforderung();
    const anforderung = gewaehlt?.id === id ? gewaehlt : this.requirementBlock()?.requirement();
    return anforderung?.id === id ? anforderung : null;
  });
  protected readonly anforderungAnzeigeText = computed(() => {
    const anforderung = this.anforderungAnzeige();
    return anforderung ? [anforderung.key, anforderung.title].filter((teil) => !!teil).join(' · ') : '';
  });
  /** Quelle-Badge: Jira-Key der Story, sonst Key der verknüpften Anforderung, beim Anlegen aus einer neuen der Jira-Key ihres Entwurfs. */
  protected readonly sourceBadgeKey = computed(
    () => this.linkedStoryKey() ?? this.requirementBlock()?.requirement()?.key ?? this.newRequirementJiraKey,
  );
  /**
   * NEX-1052 Follow-up: das Step-Table haelt seinen eigenen `steps`-Signal-Zustand,
   * der beim `ngOnInit` einmal aus `initialSteps` geseedet wird. Damit ein vom
   * Drawer emittiertes TDB-Delta die Chip-Anzeige sofort aktualisiert, patchen wir
   * hier zusaetzlich direkt in die Tabelle.
   */
  private readonly stepTable = viewChild<TestStepTableComponent>('stepTable');
  private readonly detailsHistorie = viewChild(TestCaseDetailsHistorieComponent);
  readonly currentUserId = signal<string | null>(null);
  readonly showLinkModal = signal(false);
  /**
   * NEX-1051: alle Testdatenblaetter des Projekts. Wird einmal beim Formular-Load
   * gefetched und an das Step-Table gereicht, damit dort die Modul-TDB-Chips und der
   * Override-Block rendern koennen.
   */
  readonly availableSheets = signal<readonly Testdatenblatt[]>([]);
  readonly refreshing = signal(false);
  readonly refreshError = signal(false);

  /**
   * App-Modal-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). Ein Modal bedient beide
   * Flows — Verlassen-Guard und Refresh-aus-Anforderung — über dynamische Texte.
   */
  readonly showDeactivateConfirm = signal(false);
  readonly deactivateMessage = signal('testCases.form.unsavedChanges');
  readonly deactivateConfirmLabel = signal('common.unsavedChanges.leave');
  readonly deactivateCancelLabel = signal('common.unsavedChanges.stay');
  private readonly deactivateConfirm$ = new Subject<boolean>();

  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));
  readonly loadedReviewerIds = signal<string[]>([]);

  private auswahllisten: TestCaseSelectionList[] | null = null;

  readonly fieldLengths = FIELD_LENGTHS;

  readonly form = this.fb.group({
    title: ['', [requiredNotBlank, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
    description: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    testGoal: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    preconditions: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    requirementId: [''],
    storyJiraKey: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    bugJiraKey: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    notes: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    type: this.fb.control<string>('FUNCTIONAL', Validators.required),
    testType: this.fb.control<string>('SYSTEM', Validators.required),
    priority: this.fb.control<TestCasePriority>('MEDIUM'),
    status: this.fb.control<string>('DRAFT', Validators.required),
    assigneeUserId: ['', Validators.required],
    reviewerIds: this.fb.control<string[]>([]),
    tags: this.fb.control<string[]>([]),
    automationStatus: this.fb.control<TestCaseAutomationStatusKey>('MANUAL'),
    automationFramework: this.fb.control<TestCaseAutomationFramework | null>(null),
    automationLocator: this.fb.control<string>(''),
  });

  readonly basicsSectionKeys = {
    title: 'testCases.form.sections.basics.title',
    subtitle: 'testCases.form.sections.basics.subtitle',
  };

  /** Rohwerte auch im gesperrten Formular (PROJECT_READER): der KI-Qualitätscheck prüft den aktuellen Stand. */
  private readonly formWerte = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });
  readonly kiTitel = computed(() => this.formWerte().title);
  readonly notizGesetzt = computed(() => !!this.formWerte().notes?.trim());
  readonly kiVorbedingung = computed(() => this.formWerte().preconditions);
  readonly testCaseAssigneeName = computed(
    () => this.personOptions().find((o) => o.id === this.formWerte().assigneeUserId)?.displayName ?? null,
  );

  /** Absprung „Befunde ansehen“ aus dem Postfach: gespeichertes Ergebnis zeigen statt neu anfragen. */
  readonly kiAnfrageId = toSignal(this.route.queryParamMap.pipe(map((params) => params.get('kiAnfrage'))), {
    initialValue: null,
  });
  private readonly kiCheck = viewChild(KiQualitaetsCheckComponent);
  private readonly kiOffeneBefunde = computed(() => this.kiCheck()?.offeneBefunde() ?? []);
  readonly kiBefundeAmSchritt = computed(() => this.kiCheck()?.befundeAmSchritt() ?? []);
  readonly kiVorbedingungBefund = computed(() => this.kiOffeneBefunde().some((ziel) => ziel.feld === 'VORBEDINGUNG'));
  readonly kiVorbedingungGeaendert = signal(false);

  /**
   * Value-based dirty tracking (NEX-603): the form value plus the buffered step
   * draft form the tracked content. Reverting a change back to the loaded state
   * clears the dirty pill and re-disables save (NEX-535/NEX-599). The tracker's
   * result is mirrored onto {@code form.dirty} so template, guard and save-bar
   * keep reading the single Angular flag.
   */
  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({
      steps: this.stepsSignature(this.draftSteps()),
      links: this.linksSignature(this.pendingLinkDrafts()),
      linkDeletions: [...this.pendingLinkDeletions()].sort().join(','),
    }),
  });

  private sentState: { form: unknown; steps: TestStep[] } | null = null;

  saveDisabled(): boolean {
    return this.saving() || this.loading() || this.loadError();
  }

  /** Halbe Bedingungen bleiben aus draftSteps heraus, damit nur vollständige gesendet werden; sie zählen trotzdem als Änderung. */
  stepTableHasUnsavedInput(): boolean {
    const table = this.stepTable();
    return !!table && (table.openRowChanged() || table.hasIncompleteConditionDraft());
  }

  hasUnsavedChanges(): boolean {
    return (
      !this.isReadOnly() && (this.form.dirty || this.stepTableHasUnsavedInput() || this.uebergabeEntwurfUngesendet())
    );
  }

  private uebergabeEntwurfUngesendet(): boolean {
    const entwurf = this.detailsHistorie()?.entwurf();
    return !!entwurf && (!!entwurf.userId || !!entwurf.nachricht.trim());
  }

  isDirtyView(): boolean {
    return !this.isReadOnly() && !this.loadError() && (this.form.dirty || this.stepTableHasUnsavedInput());
  }

  runDisabled(): boolean {
    const card = this.automationCard();
    return !this.isEdit || this.loading() || !card || card.runBlocked();
  }

  protected get backAriaLabelKey(): string {
    return this.returnToRequirementId ? 'testCases.form.backToRequirementAriaLabel' : 'testCases.form.backAriaLabel';
  }

  private jiraKeyOfNewRequirement(): string | null {
    if (this.isEdit || this.returnToRequirementId !== NEW_REQUIREMENT_DRAFT_ID) return null;
    return (
      this.draftStore
        .restore(NEW_REQUIREMENT_DRAFT_ID, this.findParam('projectId') ?? undefined)
        ?.form.jiraIssueKey?.trim() || null
    );
  }

  oeffneAnforderungAuswahl(suchtext = ''): void {
    if (this.isReadOnly()) return;
    this.anforderungSuchtext.set(suchtext);
    this.anforderungAuswahlOffen.set(true);
  }

  onAnforderungTaste(event: KeyboardEvent): void {
    if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    this.oeffneAnforderungAuswahl();
  }

  onAnforderungEingabe(event: Event): void {
    const feld = event.target as HTMLInputElement;
    const suchtext = feld.value;
    feld.value = this.anforderungAnzeigeText();
    this.oeffneAnforderungAuswahl(suchtext);
  }

  onAnforderungGewaehlt(anforderung: Requirement): void {
    const control = this.form.controls.requirementId;
    this.gewaehlteAnforderung.set(anforderung);
    control.setValue(anforderung.id);
    control.markAsDirty();
    control.markAsTouched();
    this.schliesseAnforderungAuswahl();
  }

  entferneAnforderung(): void {
    const control = this.form.controls.requirementId;
    this.gewaehlteAnforderung.set(null);
    control.setValue('');
    control.markAsDirty();
    this.fokussiereAnforderungFeld();
  }

  schliesseAnforderungAuswahl(): void {
    this.anforderungAuswahlOffen.set(false);
    this.fokussiereAnforderungFeld();
  }

  private fokussiereAnforderungFeld(): void {
    afterNextRender(() => this.anforderungFeld()?.nativeElement.focus(), { injector: this.injector });
  }

  private markMissingRequiredFieldsAsTouched(): void {
    for (const controlName of PFLICHTFELDER) {
      const control = this.form.get(controlName);
      if (control?.hasError('required')) {
        control.markAsTouched();
      }
    }
  }

  /**
   * Übernimmt den aktuellen Draft-Schritt-Zustand vom Table (NEX-599 Buffered-Save)
   * und synct dirty/pristine gegen die Content-Signature nach Load. Toggle-on-off
   * eines Bibliotheks-Schritts revertiert damit den Dirty-State (NEX-535, NEX-599).
   */
  onStepsChanged(steps?: TestStep[]): void {
    if (steps !== undefined) {
      this.draftSteps.set(steps);
    }
    this.syncDirtyFromContent();
  }

  onDraftLinksChanged(drafts: CreateTestCaseLinkRequest[]): void {
    this.pendingLinkDrafts.set(drafts);
    this.syncDirtyFromContent();
  }

  onPendingDeletionsChanged(ids: string[]): void {
    this.pendingLinkDeletions.set(ids);
    this.syncDirtyFromContent();
  }

  private linksSignature(list: CreateTestCaseLinkRequest[]): string {
    return JSON.stringify(list.map((l) => ({ type: l.type, ref: l.reference, label: l.displayLabel ?? null })));
  }

  onHandoverChainChanged(): void {
    this.refreshLoadedVersion();
  }

  onModuleSaved(): void {
    this.refreshLoadedVersion();
  }

  // Übergabekette und Modul-Speichern erhöhen serverseitig die Testfall-Version um eins.
  // Nur die Version nachziehen, damit Eingaben im Formular erhalten bleiben. Liegt der Server
  // weiter vorn, hat jemand anderes gespeichert: dann bleibt die alte Version, damit das
  // nächste Speichern den Konflikt meldet statt die fremde Änderung zu überschreiben.
  private refreshLoadedVersion(): void {
    const id = this.savedTestCaseId;
    if (!id) return;
    this.testCaseService
      .getById(this.projectId, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tc) => {
          if (this.loadedVersion !== null && tc.version === this.loadedVersion + 1) this.loadedVersion = tc.version;
        },
        // Nachladen fehlgeschlagen: alte Version bleibt, das nächste Speichern meldet ehrlich den Konflikt.
        error: () => undefined,
      });
  }

  private snapshotInitialState(): void {
    this.dirtyTracker.reset();
    this.form.markAsPristine();
  }

  // Eingaben während des Speicherns sind nicht gesendet und bleiben deshalb ungespeichert.
  private baselineToSentState(): void {
    const sent = this.sentState;
    if (!sent) {
      this.snapshotInitialState();
      return;
    }
    this.dirtyTracker.resetTo(sent.form, {
      steps: this.stepsSignature(sent.steps),
      links: this.linksSignature(this.pendingLinkDrafts()),
      linkDeletions: [...this.pendingLinkDeletions()].sort().join(','),
    });
    this.syncDirtyFromContent();
  }

  /**
   * Mirrors value-based dirtiness onto {@code form.dirty}, so template, save-bar
   * and the can-deactivate guard keep reading the single Angular flag while the
   * comparison itself is value-based (reverting a change clears it — NEX-603).
   */
  private syncDirtyFromContent(): void {
    if (this.dirtyTracker.isDirtyNow()) {
      this.form.markAsDirty();
    } else {
      this.form.markAsPristine();
    }
  }

  private stepsSignature(steps: TestStep[]): string {
    return JSON.stringify(
      steps.map((s) => ({
        id: s.id,
        kind: s.kind,
        action: s.action ?? null,
        expectedResult: s.expectedResult ?? null,
        notes: s.notes ?? null,
        tcdl: s.tcdl ?? null,
        status: s.status,
        title: s.title ?? null,
        libraryStepId: s.libraryStepId ?? null,
        calledModuleId: s.calledModuleId ?? null,
        // NEX-1051: TDB-Override am Modulaufruf gehoert zum Dirty-Vergleich.
        dataSheetId: s.dataSheetId ?? null,
        dataSheetFilters: s.dataSheetFilters ?? null,
        stepCondition: s.stepCondition ?? null,
        stepConditions: s.stepConditions ?? null,
      })),
    );
  }

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.requireParam('projectId');
    this.savedTestCaseId = this.testCaseId;

    effect(() => {
      const readOnly = this.isReadOnly();
      untracked(() => (readOnly ? this.form.disable() : this.form.enable()));
    });

    // matInput lässt aria-invalid bei leerem Pflichtfeld bewusst weg; Screenreader sollen den Fehler trotzdem hören.
    afterEveryRender({
      write: () => {
        const feld = this.titelFeld()?.nativeElement;
        if (!feld) return;
        const titel = this.form.controls.title;
        if (titel.invalid && titel.touched) feld.setAttribute('aria-invalid', 'true');
        else if (feld.getAttribute('aria-invalid') === 'true') feld.removeAttribute('aria-invalid');
      },
    });
  }

  // Das Formular hat keinen eigenen Absenden-Knopf; Knöpfe ohne type in Dialogen darin würden sonst speichern.
  verhindereUngewolltesAbsenden(event: MouseEvent): void {
    const knopf = (event.target as Element | null)?.closest('button');
    if (knopf?.type === 'submit' && knopf.form === event.currentTarget) event.preventDefault();
  }

  ngOnInit(): void {
    this.form.controls.requirementId.valueChanges
      .pipe(startWith(this.form.controls.requirementId.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => this.linkedRequirementId.set(id.trim() || null));

    this.form.controls.storyJiraKey.valueChanges
      .pipe(startWith(this.form.controls.storyJiraKey.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((key) => this.linkedStoryKey.set(key.trim() || null));

    this.loadUsers();
    this.loadAvailableSheets();

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncDirtyFromContent());

    if (this.isEdit && this.testCaseId) {
      this.loadTestCase(this.testCaseId);
    } else {
      this.stepsReady.set(true);
      const requirementId = this.route.snapshot.queryParamMap.get('requirementId');
      if (requirementId) {
        this.preFillFromRequirement(requirementId);
      } else {
        this.snapshotInitialState();
      }
    }

    // Angular's default RouteReuseStrategy behält die Instanz nur, wenn dieselbe
    // Route mit anderen Parametern aktiv bleibt (etwa /:id/edit → anderes :id oder
    // geänderte Query). Wir hören auf paramMap/queryParamMap, um solche Wechsel
    // innerhalb derselben Instanz zu erkennen. skip(1) überspringt die initiale
    // synchrone Emission, die bereits durch die Snapshot-basierte Initialisierung
    // oben abgedeckt ist.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        skip(1),
        map(([pm, qp]) => ({ id: pm.get('id'), requirementId: qp.get('requirementId') })),
        distinctUntilChanged((a, b) => a.id === b.id && a.requirementId === b.requirementId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ id, requirementId }) => {
        if (id) {
          this.savedTestCaseId = id;
          this.loadTestCase(id);
          return;
        }
        this.stepsReady.set(true);
        this.resetCreateState();
        if (requirementId) {
          this.preFillFromRequirement(requirementId);
        } else {
          this.snapshotInitialState();
        }
      });
  }

  private resetCreateState(): void {
    this.savedTestCaseId = null;
    this.loadedFreetext = null;
    this.loadedVersion = null;
    this.saving.set(false);
    this.saveError.set(false);
    this.saveErrorText.set(null);
    this.saveStale.set(false);
    this.form.reset({
      title: '',
      description: '',
      testGoal: '',
      preconditions: '',
      requirementId: '',
      storyJiraKey: '',
      bugJiraKey: '',
      notes: '',
      type: 'FUNCTIONAL',
      testType: 'SYSTEM',
      priority: 'MEDIUM',
      status: 'DRAFT',
      assigneeUserId: '',
      reviewerIds: [],
      tags: [],
      automationStatus: 'MANUAL',
      automationFramework: null,
      automationLocator: '',
    });
    this.startwerteAusAuswahllisten();
    this.draftSteps.set([]);
    this.pendingLinkDrafts.set([]);
    this.pendingLinkDeletions.set([]);
    this.loadedReviewerIds.set([]);
  }

  onAuswahllistenGeladen(listen: TestCaseSelectionList[]): void {
    this.auswahllisten = listen;
    if (this.isEdit) return;
    const unveraendert = !this.form.dirty;
    this.startwerteAusAuswahllisten();
    if (unveraendert) this.snapshotInitialState();
  }

  /** Beim Anlegen gilt der heutige Startwert nur, solange er in der Projektliste aktiv ist; sonst der erste aktive Wert. */
  private startwerteAusAuswahllisten(): void {
    for (const liste of this.auswahllisten ?? []) {
      const control = this.form.controls[AUSWAHLLISTE_ZU_CONTROL[liste.listKey]];
      const aktiv = liste.werte.filter((w) => w.active).map((w) => w.key);
      if (control && !aktiv.includes(control.value)) control.setValue(aktiv[0] ?? '');
    }
  }

  onSave(): void {
    if (this.saving()) return;
    if (this.savedTestCaseId && !this.form.dirty && !this.stepTableHasUnsavedInput()) {
      this.saveToast.show(this.translateService.instant('testCases.form.noChanges'));
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.fokusAufErstesFehlerhaftesFeld();
      return;
    }
    if (this.stepTable()?.fokussiereErstenFehler()) return;
    this.saving.set(true);
    this.saveError.set(false);
    this.saveErrorText.set(null);
    this.saveStale.set(false);
    this.missingRequirementKey.set(null);
    this.duplicateRequirementLink.set(false);
    const raw = this.form.getRawValue();
    const ids = {
      requirementId: raw.requirementId.trim(),
      storyJiraKey: raw.storyJiraKey.trim(),
      bugJiraKey: raw.bugJiraKey.trim(),
    };
    const request = toSaveFullRequest(raw as TestCaseFormRaw, ids, this.draftSteps(), this.loadedFreetext);
    this.sentState = { form: raw, steps: this.draftSteps() };

    const editingId = this.savedTestCaseId;
    const request$ = editingId
      ? this.testCaseService.saveFull(this.projectId, editingId, { ...request, expectedVersion: this.loadedVersion })
      : this.testCaseService.createFull(this.projectId, request);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.stepTable()?.showSecretColumnError(null);
        this.loadedFreetext = loadedFreetextOf(raw);
        this.loadedVersion = response.testCase?.version ?? this.loadedVersion;
        if (this.isEdit && response.testCase) this.testCase.set(response.testCase);
        this.applyClientIdMapping(response);
        const drafts = this.pendingLinkDrafts();
        const deletions = this.pendingLinkDeletions();
        const persistedId = response.testCase?.id ?? this.savedTestCaseId;
        this.savedTestCaseId = persistedId;
        if ((drafts.length === 0 && deletions.length === 0) || !persistedId) {
          this.flushReviewersThenFinish(persistedId);
          return;
        }
        // NEX-714-Review (fboudouhiST): pro Request eigenen catchError-Wrapper
        // statt gemeinsamem forkJoin-Error, damit erfolgreich abgeschlossene
        // Operationen nicht bei einem Teilausfall des naechsten Save-Versuchs
        // dupliziert werden.
        const draftOps$ = drafts.map((request) =>
          this.linksService.create(this.projectId, persistedId, request).pipe(
            map((link) => ({ ok: true as const, kind: 'draft' as const, request, link })),
            catchError((err: unknown) =>
              of({
                ok: false as const,
                kind: 'draft' as const,
                request,
                missingKey: missingRequirementKey(request, err),
                duplicate: isDuplicateRequirementLink(request, err),
              }),
            ),
          ),
        );
        const deletionOps$ = deletions.map((linkId) =>
          this.linksService.delete(this.projectId, persistedId, linkId).pipe(
            map(() => ({ ok: true as const, kind: 'deletion' as const, linkId })),
            catchError(() => of({ ok: false as const, kind: 'deletion' as const, linkId })),
          ),
        );
        forkJoin([...draftOps$, ...deletionOps$])
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((results) => {
            const failedDrafts: FailedLinkDraft[] = [];
            const failedDeletions: string[] = [];
            const successfulDrafts: SavedLinkDraft[] = [];
            const successfulDeletions: string[] = [];
            let missingKey: string | null = null;
            let duplicate = false;
            for (const r of results) {
              if (r.kind === 'draft') {
                if (r.ok) {
                  successfulDrafts.push({ request: r.request, link: r.link });
                } else {
                  failedDrafts.push({ request: r.request, reason: linkSaveFailure(r) });
                  missingKey ??= r.missingKey;
                  duplicate ||= r.duplicate;
                }
              } else {
                (r.ok ? successfulDeletions : failedDeletions).push(r.linkId);
              }
            }
            if (failedDrafts.length === 0 && failedDeletions.length === 0) {
              this.linksBlock()?.resetPending();
              this.pendingLinkDrafts.set([]);
              this.pendingLinkDeletions.set([]);
              this.flushReviewersThenFinish(persistedId);
              return;
            }
            // Child-State reconcilen: erfolgreiche Ops raus, sonst emittiert
            // der Child beim naechsten Add wieder die volle Draft-Liste und
            // ueberschreibt pendingLinkDrafts mit den bereits angelegten
            // Requests -> doppelte Links beim naechsten Save (NEX-714 R2).
            this.linksBlock()?.applySaveResult(
              successfulDrafts,
              successfulDeletions,
              failedDrafts,
              failedDeletions.length > 0,
            );
            this.pendingLinkDrafts.set(failedDrafts.map((f) => f.request));
            this.pendingLinkDeletions.set(failedDeletions);
            this.saving.set(false);
            this.saveError.set(true);
            this.missingRequirementKey.set(missingKey);
            this.duplicateRequirementLink.set(duplicate);
          });
      },
      // Draft-State bleibt intakt: draftSteps + Form-Werte werden nicht angefasst.
      error: (err: unknown) => {
        this.zeigeServerFeldFehler(err);
        this.stepTable()?.showSecretColumnError(secretColumnError(err));
        const stale = isStaleVersion(err);
        this.saveStale.set(stale);
        this.saveErrorText.set(stale ? null : this.saveErrorReason(err));
        this.saving.set(false);
        this.saveError.set(true);
      },
    });
  }

  private saveErrorReason(err: unknown): string | null {
    return isSessionEnded(err) ? this.translateService.instant('fehler.sitzungBeendet') : serverMessage(err);
  }

  private zeigeServerFeldFehler(err: unknown): void {
    const fehler = serverFeldFehler(err);
    if (!fehler) return;
    const control = this.form.controls[fehler.feld];
    const key = SERVER_FEHLER_TEXT[fehler.code];
    control.setErrors({ server: key ? this.translateService.instant(key) : fehler.text });
    control.markAsTouched();
    this.fokusAufFeld(fehler.feld);
  }

  private formularFelder(): HTMLElement[] {
    const formular = this.host.nativeElement.querySelector<HTMLElement>('form.tc-body');
    return Array.from(formular?.querySelectorAll<HTMLElement>('[formcontrolname], [data-control-name]') ?? []);
  }

  private controlName(feld: HTMLElement): string {
    return feld.getAttribute('formcontrolname') ?? feld.getAttribute('data-control-name') ?? '';
  }

  private fokussiere(feld: HTMLElement | undefined): void {
    const ziel = feld?.matches(FOKUSSIERBAR) ? feld : feld?.querySelector<HTMLElement>(FOKUSSIERBAR);
    ziel?.focus();
  }

  private fokusAufFeld(controlName: string): void {
    this.fokussiere(this.formularFelder().find((element) => this.controlName(element) === controlName));
  }

  private fokusAufErstesFehlerhaftesFeld(): void {
    this.fokussiere(this.formularFelder().find((element) => this.form.get(this.controlName(element))?.invalid));
  }

  private flushReviewersThenFinish(persistedId: string | null): void {
    if (!persistedId) {
      this.finishSaveSuccess();
      return;
    }
    const current = this.form.controls.reviewerIds.value;
    const baseline = this.loadedReviewerIds();
    const added = current.filter((id) => !baseline.includes(id));
    const removed = baseline.filter((id) => !current.includes(id));
    if (added.length === 0 && removed.length === 0) {
      this.finishSaveSuccess();
      return;
    }
    const ops: Observable<boolean>[] = [
      ...added.map((id) =>
        this.reviewerService.add(this.projectId, 'test-cases', persistedId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
      ...removed.map((id) =>
        this.reviewerService.remove(this.projectId, 'test-cases', persistedId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
    ];
    forkJoin(ops)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        if (results.every((ok) => ok)) {
          this.loadedReviewerIds.set(current);
          this.finishSaveSuccess();
        } else {
          this.saving.set(false);
          this.saveError.set(true);
        }
      });
  }

  private finishSaveSuccess(): void {
    this.saving.set(false);
    this.pendingLinkDrafts.set([]);
    this.pendingLinkDeletions.set([]);
    this.linksBlock()?.resetPending();
    this.baselineToSentState();
    if (this.isEdit) {
      this.linksBlock()?.reload();
      this.saveToast.show(this.translateService.instant('testCases.form.saved'));
      return;
    }
    this.saveToast.show(this.translateService.instant('testCases.form.created'));
    const anfId = this.returnToRequirementId;
    const id = this.savedTestCaseId;
    if (anfId || !id) {
      if (anfId && id) this.draftStore.rememberCreatedTestCaseId(anfId, id, this.projectId);
      this.navigateToList();
      return;
    }
    this.router.navigate(['/projects', this.projectId, 'test-cases', id, 'edit'], { replaceUrl: true });
  }

  private applyClientIdMapping(response: SaveFullTestCaseResponse): void {
    const mapping = response.clientIdMapping ?? {};
    const current = withServerIds(this.draftSteps(), mapping);
    const sent = this.sentState ? withServerIds(this.sentState.steps, mapping) : current;
    const saved = savedConditionsNotChangedSince(current, sent, response.steps);
    this.draftSteps.set(withSavedStepConditions(current, saved));
    if (this.sentState) this.sentState = { ...this.sentState, steps: withSavedStepConditions(sent, response.steps) };
    this.stepTable()?.applyClientIdMapping(mapping);
    this.stepTable()?.applySavedConditions(saved);
    this.kiCheck()?.idsUmschluesseln(mapping);
  }

  onCancel(): void {
    this.navigateToList();
  }

  /**
   * NEX-644: Sprung aus einer MODULE_REF-Zeile zum eingebundenen Modul. Der Wechsel
   * verlässt den Testfall-Editor, daher greift der CanDeactivate-Guard für die
   * Dirty-Bestätigung — hier genügt die Navigation.
   */
  onOpenModule(moduleId: string): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', moduleId, 'edit']);
  }

  /**
   * NEX-686: Bearbeiten eines eingebundenen Moduls im Right-Drawer, ohne den Testfall zu
   * verlassen. Die Modul-Zeilen spiegeln die Änderung nach dem Save live (NEX-691 Store).
   */
  onEditModule(event: { moduleId: string; stepId: string | null }): void {
    this.editingModuleId.set(event.moduleId);
  }

  onModuleDrawerClosed(): void {
    this.editingModuleId.set(null);
  }

  /**
   * Das im Drawer getrennte Blatt gilt an keinem Aufruf dieses Moduls mehr (NEX-1796). Die
   * Bedingungen bleiben, der Testfall gilt als geaendert und speichert die Aufrufe ohne Blatt.
   */
  onModuleSheetUnlinked({ moduleId, sheetId }: { moduleId: string; sheetId: string }): void {
    const affected = this.draftSteps().filter(
      (s) => s.kind === 'MODULE_REF' && s.calledModuleId === moduleId && s.dataSheetId === sheetId,
    );
    if (affected.length === 0) return;
    const ids = new Set(affected.map((s) => s.id));
    this.draftSteps.update((steps) =>
      steps.map((s) => (ids.has(s.id) ? { ...s, dataSheetId: null, dataSheetFilters: null } : s)),
    );
    affected.forEach((step) => this.stepTable()?.onBlockOverrideReset(step));
    this.syncDirtyFromContent();
  }

  /** „Übernehmen“ zur Vorbedingung im KI-Qualitätscheck; Schrittbefunde übernimmt die Tabelle selbst. */
  onKiVerbessern(aenderung: KiQualitaetAenderung): void {
    if (this.isReadOnly()) return;
    this.form.controls.preconditions.setValue(aenderung.wert);
    this.kiVorbedingungGeaendert.set(true);
  }

  confirmDeactivate(): Observable<boolean> {
    return this.openConfirm({
      message: 'testCases.form.unsavedChanges',
      confirmLabel: 'common.unsavedChanges.leave',
      cancelLabel: 'common.unsavedChanges.stay',
    });
  }

  /**
   * Zeigt die geteilte App-Modal mit den übergebenen i18n-Keys und liefert die
   * User-Entscheidung als einmaliges Observable (NEX-814).
   */
  private openConfirm(config: { message: string; confirmLabel: string; cancelLabel: string }): Observable<boolean> {
    this.deactivateMessage.set(config.message);
    this.deactivateConfirmLabel.set(config.confirmLabel);
    this.deactivateCancelLabel.set(config.cancelLabel);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  refreshFromRequirement(): void {
    if (!this.isEdit || !this.testCaseId || !this.linkedStoryKey()) {
      return;
    }
    // Bei ungespeicherten Eingaben warnen, dass der Refresh Titel/Beschreibung
    // im Formular ersetzt — sonst gingen Edits unbemerkt verloren.
    const message = this.form.dirty ? 'testCases.form.source.confirmDirty' : 'testCases.form.source.confirm';
    this.openConfirm({
      message,
      confirmLabel: 'testCases.form.source.confirmButton',
      cancelLabel: 'common.unsavedChanges.stay',
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.doRefreshFromRequirement();
        }
      });
  }

  private doRefreshFromRequirement(): void {
    if (!this.testCaseId) {
      return;
    }
    this.refreshing.set(true);
    this.refreshError.set(false);
    this.testCaseService
      .refreshFromRequirement(this.projectId, this.testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.testCase.set(updated);
          this.loadedVersion = updated.version;
          this.form.patchValue({
            title: updated.title,
            description: updated.description ?? '',
          });
          this.loadedFreetext = loadedFreetextOf({
            description: updated.description ?? '',
            testGoal: updated.testGoal ?? '',
            preconditions: updated.preconditions ?? '',
          });
          this.form.markAsPristine();
          this.snapshotInitialState();
          this.refreshing.set(false);
        },
        error: () => {
          this.refreshing.set(false);
          this.refreshError.set(true);
        },
      });
  }

  /**
   * NEX-1051: laedt alle Testdatenblaetter des Projekts. Ein Fehler wird still
   * geschluckt — ohne Blaetter zeigt das Step-Table keine Chips, blockiert aber
   * das Formular nicht. Drawer-Aenderungen kommen nicht hierueber zurueck, sondern
   * als `sheetsChanged`-Snapshot direkt in `availableSheets`.
   */
  loadAvailableSheets(): void {
    this.testdatenblattService
      .list(this.projectId)
      .pipe(
        catchError(() => of([] as Testdatenblatt[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sheets) => this.availableSheets.set(sheets));
  }

  private loadUsers(): void {
    // NEX-822: Projekt-Mitglieder statt des TENANT_ADMIN-only Customers/Users-Endpunkts —
    // ein Projekt-Lead darf diesen Endpunkt aufrufen und bekommt so Reviewer-Kandidaten.
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('testCases.form.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => {
        this.users.set(members);
        this.loadCurrentUserId();
        if (!this.isEdit) {
          this.applyAssigneeDefault();
        }
      });
  }

  private loadCurrentUserId(): void {
    if (this.currentUserId() !== null) return;
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (profile) this.currentUserId.set(profile.id);
      });
  }

  /**
   * NEX-824: prefills the assignee with the current user when creating a new test
   * case. Runs after the project members are loaded so `personOptions()` already
   * reflects the PENDING/inactive filtering from NEX-822 — absent from those
   * options, the field stays empty and required validation forces an explicit pick.
   */
  private applyAssigneeDefault(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (!profile) return;
        this.currentUserId.set(profile.id);
        prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, profile.id, this.personOptions());
        this.snapshotInitialState();
      });
  }

  private loadTestCase(id: string): void {
    this.kiVorbedingungGeaendert.set(false);
    this.loading.set(true);
    forkJoin({
      tc: this.testCaseService.getById(this.projectId, id),
      reviewers: this.reviewerService
        .list(this.projectId, 'test-cases', id)
        .pipe(catchError(() => of([] as ReviewerDTO[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tc, reviewers }) => {
          this.testCase.set(tc);
          this.loadedVersion = tc.version;
          const reviewerIds = reviewers.map((r) => r.reviewerUserId);
          this.loadedReviewerIds.set(reviewerIds);
          this.form.patchValue({
            title: tc.title,
            description: tc.description ?? '',
            testGoal: tc.testGoal ?? '',
            preconditions: tc.preconditions ?? '',
            requirementId: tc.requirementId ?? '',
            storyJiraKey: tc.storyJiraKey ?? '',
            bugJiraKey: tc.bugJiraKey ?? '',
            notes: tc.notes ?? '',
            type: tc.type ?? 'FUNCTIONAL',
            testType: tc.testType ?? 'SYSTEM',
            priority: tc.priority ?? 'MEDIUM',
            status: tc.status ?? 'DRAFT',
            assigneeUserId: tc.assigneeUserId ?? '',
            reviewerIds,
            tags: tc.tags ?? [],
            automationStatus: tc.automationStatus ?? 'MANUAL',
            automationFramework: tc.automationFramework ?? null,
            automationLocator: tc.automationLocator ?? '',
          });
          this.loadedFreetext = loadedFreetextOf(this.form.getRawValue());
          this.form.markAsPristine();
          this.markMissingRequiredFieldsAsTouched();
          this.loading.set(false);
          this.loadDraftSteps(id);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadDraftSteps(testCaseId: string): void {
    // Kein Silent-Fallback auf []: sonst würde ein Save den bestehenden BE-Zustand
    // per PUT /full leer überschreiben (Vollersatz-Semantik).
    this.testStepService
      .list(this.projectId, testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.draftSteps.set(steps);
          this.stepsReady.set(true);
          this.snapshotInitialState();
        },
        error: () => {
          this.loadError.set(true);
        },
      });
  }

  private preFillFromRequirement(requirementId: string): void {
    this.form.patchValue({ requirementId });
    this.form.markAsPristine();
    this.snapshotInitialState();
  }

  private navigateToList(): void {
    const anfId = this.returnToRequirementId;
    if (anfId) {
      const anforderung = anfId === NEW_REQUIREMENT_DRAFT_ID ? ['new'] : [anfId, 'edit'];
      this.router.navigate(['/projects', this.projectId, 'requirements', ...anforderung], {
        queryParams: { from: 'anforderung', anfId },
      });
      return;
    }
    this.router.navigate(['/projects', this.projectId, 'test-cases']);
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
